import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const CurrencyContext = createContext(null);

const LS_KEY = "jm_currency";

// Locale → default currency (checked before timezone).
const LOCALE_MAP = {
  "en-US": "USD", "en-GB": "GBP", "en-CA": "CAD", "en-AU": "AUD", "en-NZ": "NZD",
  "en-IE": "EUR", "fr": "EUR", "de": "EUR", "nl": "EUR", "fr-BE": "EUR",
  "fr-CH": "CHF", "de-CH": "CHF", "ar-AE": "AED", "en-AE": "AED", "en-SG": "SGD",
};

// Timezone → currency fallback.
const CA_TZ = ["Toronto", "Vancouver", "Winnipeg", "Halifax", "Edmonton", "Moncton", "St_Johns", "Montreal", "Ottawa", "Calgary"];

function detectCurrency() {
  try {
    const lang = navigator.language || "en-US";
    if (LOCALE_MAP[lang]) return LOCALE_MAP[lang];
    const short = lang.split("-")[0];
    if (LOCALE_MAP[short]) return LOCALE_MAP[short];
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.startsWith("Australia/")) return "AUD";
    if (tz.startsWith("Pacific/Auckland")) return "NZD";
    if (tz === "Europe/London") return "GBP";
    if (tz === "Europe/Zurich") return "CHF";
    if (tz === "Asia/Dubai") return "AED";
    if (tz === "Asia/Singapore") return "SGD";
    if (tz.startsWith("Europe/")) return "EUR";
    if (CA_TZ.some((c) => tz.includes(c))) return "CAD";
    if (tz.startsWith("America/")) return "USD";
  } catch { /* ignore */ }
  return "USD";
}

export function CurrencyProvider({ children }) {
  const [data, setData] = useState({ rates: { USD: 1 }, currencies: [{ code: "USD", symbol: "$", name: "US Dollar" }] });
  const [code, setCode] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || detectCurrency(); } catch { return "USD"; }
  });

  useEffect(() => {
    api.get("/currencies").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, code); } catch { /* ignore */ }
  }, [code]);

  const value = useMemo(() => {
    const rates = data.rates || { USD: 1 };
    const meta = (data.currencies || []).find((c) => c.code === code) || { code: "USD", symbol: "$" };
    const rate = rates[code] || 1;
    /** Format a USD base amount in the selected currency (whole units by default). */
    const fmt = (usd, { decimals } = {}) => {
      const v = (Number(usd) || 0) * rate;
      const d = decimals !== undefined ? decimals : 0;
      const rounded = d === 0 ? Math.round(v) : Math.round(v * 100) / 100;
      return `${meta.symbol}${rounded.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
    };
    return { code, setCode, currencies: data.currencies || [], rates, fmt, converted: (usd) => (Number(usd) || 0) * rate };
  }, [data, code]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => useContext(CurrencyContext);
